import React from 'react'
import styled, { StyledProps } from 'styled-components'
import { useParentOffset } from '../../../hooks'
import { FormField } from '../../form/FormField/FormField'

export const TEST_ID_SELECTED = 'selected-item'

const bezierTimingFunction = 'cubic-bezier(0.060, 0.975, 0.195, 0.985)'
type SegmentProps = StyledProps<{ disabled?: boolean; flat?: boolean; responsive?: boolean }>

const segmentCss = ({ flat, disabled }: SegmentProps) => {
  let opacity = 1
  let pointerEvents: 'none' | 'all' = 'all'
  let padding = '4px'

  if (disabled) {
    opacity = 0.5
    pointerEvents = 'none'
  }
  if (flat) {
    padding = '0'
  }
  return {
    opacity,
    pointerEvents,
    padding,
  }
}
const SegmentStyled = styled.div<SegmentProps>`
  position: relative;
  height: 40px;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: ${props => props.theme.color.onSurfaceLowEmphasys};
  display: inline-flex;
  flex-direction: row;
  flex-grow: ${props => (props.responsive ? '1' : '0')};
  flex-shrink: 0;
  overflow: hidden;
  ${segmentCss}
`

type SegmentItemProps = StyledProps<{
  selected?: boolean
  responsive?: boolean
}>
const segmentItemCss = ({ theme, selected }: SegmentItemProps) => {
  let color = theme.color.primary600
  let cursor = 'pointer'
  if (selected) {
    color = theme.color.onPrimaryLight
    cursor = 'default'
  }
  return {
    color,
    cursor,
  }
}
const SegmentItemStyled = styled.div<SegmentItemProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0 12px;
  transition: 0.3s color ${bezierTimingFunction};
  z-index: 1;
  flex-grow: ${props => (props.responsive ? '1' : '0')};
  flex-shrink: 0;
  ${segmentItemCss}
`

export type SegmentItem = {
  id: number | string
  title: string
}

type CaretProps = StyledProps<{
  flat?: boolean
  offset: {
    left: number
    top: number
    width: number
    height: number
  } | null
}>

const caretCss = ({ theme, offset, flat }: CaretProps) => {
  if (!offset) {
    return {
      display: 'none',
    }
  }
  const backgroundColor = theme.color.primary600
  let borderRadius = '4px'

  if (flat) {
    borderRadius = '0'
  }

  return {
    width: `${offset.width}px`,
    height: `${offset.height}px`,
    left: `${offset.left}px`,
    backgroundColor,
    borderRadius,
  }
}

const CaretStyled = styled.span<CaretProps>`
  position: absolute;
  transition: 0.3s width, 0.3s left, 0.3s top, 0.2s height;
  transition-timing-function: ${bezierTimingFunction};
  padding: 0 12px;
  ${caretCss}
`

export const Segment: React.FC<{
  className?: string
  flat?: boolean
  responsive?: boolean
  label?: string
  id?: string
  items: SegmentItem[]
  disabled?: boolean
  onChange(item: string | number): void
  value?: string | number
}> = ({ id, onChange, items, label, disabled, value, flat, className, responsive }) => {
  const [parentRef, selectedRef, offset] = useParentOffset()

  return (
    <FormField
      id={id}
      label={label}
      render={({ id }) => (
        <SegmentStyled
          disabled={disabled}
          ref={parentRef}
          role={'listbox'}
          id={id}
          flat={flat}
          className={className}
          responsive={responsive}
        >
          <CaretStyled offset={offset} flat={flat} />
          {items.map(item => {
            const isSelected = item.id === value
            return (
              <SegmentItemStyled
                responsive={responsive}
                key={item.id}
                ref={isSelected ? selectedRef : null}
                data-testid={isSelected ? TEST_ID_SELECTED : undefined}
                selected={isSelected}
                onClick={() => !disabled && onChange(item.id)}
              >
                {item.title}
              </SegmentItemStyled>
            )
          })}
        </SegmentStyled>
      )}
    />
  )
}
